import { emptyDocument, type DrawingDocument, type NodeId } from '../model';
import { resolveDocument, type ResolvedScene } from '../scene';
import { tree } from './queries';
import type { TreeEntry } from './types';

export type DrawingListener = (drawing: Drawing) => void;

/**
 * The document on screen, and its resolved scene. A drawing's script is its only source of
 * truth, so this only ever swaps in a whole new document; nothing edits one in place.
 */
export class Drawing {
  private doc: DrawingDocument;
  private readonly listeners = new Set<DrawingListener>();
  private sceneCache: { doc: DrawingDocument; scene: ResolvedScene } | null = null;

  constructor(doc: DrawingDocument = emptyDocument()) {
    this.doc = doc;
  }

  get document(): DrawingDocument {
    return this.doc;
  }

  get scene(): ResolvedScene {
    if (this.sceneCache?.doc !== this.doc) {
      this.sceneCache = { doc: this.doc, scene: resolveDocument(this.doc) };
    }
    return this.sceneCache.scene;
  }

  subscribe(listener: DrawingListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  load(doc: DrawingDocument): void {
    this.doc = doc;
    for (const listener of this.listeners) listener(this);
  }

  tree(parentId: NodeId | null = null): TreeEntry[] {
    return tree(this.doc, this.scene, parentId);
  }
}
